import { useCreateAssign } from '@vindr/services/actions/assign.action';
import { ASSIGNED_TYPE } from '@vindr/services/constants/assigned.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { Button, Col, Form, Modal, Row, Select } from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountSearch from '../AccountSearch';
import GroupSearch from './GroupSearch';
import { validateEmptyAssign } from './components/validateEmptyAssign';
import Assign from './index';
import useAccountColumns from './useAccountColumns';
import useGroupColumns from './useGroupColumns';
import { Table } from '@vindr/ui';

const AssigningForm = ({
  order,
  onFinish,
  onCancel,
  message,
}: {
  order: Parameters<typeof Assign>['0']['order'];
  onFinish: Parameters<typeof Assign>['0']['onCancel'];
  onCancel: Parameters<typeof Assign>['0']['onCancel'];
  message: Parameters<typeof Assign>['0']['message'];
}) => {
  const { t } = useTranslation('Vindoc');
  const [popupAccountSearch, setPopupAccountSearch] = useState(false);
  const [popupGroupSearch, setPopupGroupSearch] = useState(false);
  const [assignType, setAssignType] = useState(ASSIGNED_TYPE.USER);

  const [assignForm] = Form.useForm();
  // const institutions = useGetInstitutions({ offset: 0, limit: 100 })?.data?.data || [];
  const [accountList, setAccountList] = useState([]);
  const [groupList, setGroupList] = useState([]);

  const accountColumns = useAccountColumns({
    onRemove: (record) => {
      const newAccounts = [...accountList];
      setAccountList(newAccounts.filter((it) => it?.id != record?.id));
    },
  });
  const groupColumns = useGroupColumns({
    onRemove: (record) => {
      const newGroups = [...groupList];
      setGroupList(newGroups.filter((it) => it?.id != record?.id));
    },
  });

  const { mutateAsync: createAssign } = useCreateAssign();

  const handleSave = () => {
    if (accountList.length === 0 && groupList.length === 0) {
      return validateEmptyAssign({
        assignType,
        message,
      });
    }

    assignForm
      .validateFields()
      .then(() => {
        const input = {
          assign_ids:
            assignType === ASSIGNED_TYPE.USER
              ? accountList.map((it) => it?.id) || []
              : groupList.map((it) => it?.id) || [],
          order_id: order?.id,
          type: assignType,
        };

        createAssign(input, {
          onSuccess: () => {
            message.success(t(MESSAGE.SUCCESS.SCD_17001));
            onFinish();
          },
          onError: (error: any) => {
            message.error({
              content: t(
                MESSAGE.ERROR[error?.response?.data?.error_code] || MESSAGE.ERROR.EMSG_0001
              ),
            });
          },
        });
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  return (
    <div className="flex w-full flex-col">
      <div className="flex flex-col">
        <div className="flex flex-col">
          <Form
            // layout="vertical"
            requiredMark
            className="form-filter-condition"
            form={assignForm}
            labelAlign="left"
            labelCol={{ span: 9 }}
            name="form"
            wrapperCol={{ span: 15 }}
            onFinish={handleSave}
          >
            <Row gutter={[8, 8]}>
              <Col className="gutter-row" span={24}>
                <Form.Item
                  required
                  className="mb-2"
                  label={t('Assign Type')}
                  labelCol={{ span: 3 }}
                  name="type"
                  wrapperCol={{ span: 21 }}
                >
                  <Select
                    defaultValue={ASSIGNED_TYPE.USER}
                    placeholder={t('Select Type')}
                    size="small"
                    value={assignType}
                    onChange={(val) => {
                      setAssignType(val);
                      assignForm.setFieldValue('type', val);
                    }}
                  >
                    <Select.Option
                      key={ASSIGNED_TYPE.USER}
                      title={t('User')}
                      value={ASSIGNED_TYPE.USER}
                    >
                      {t('User')}
                    </Select.Option>
                    <Select.Option
                      key={ASSIGNED_TYPE.GROUP}
                      title={t('Group')}
                      value={ASSIGNED_TYPE.GROUP}
                    >
                      {t('Group')}
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </div>

      {assignType === ASSIGNED_TYPE.USER ? (
        <>
          <div className="flex items-center justify-between">
            <div className="py-2 text-xl">{t('Account List')}</div>
            {/* <Button onClick={() => setPopupAccountSearch(true)}>{t('Add')}</Button> */}
          </div>

          <Table
            columns={accountColumns}
            dataSource={[...accountList]}
            pagination={null}
            scroll={{ y: 500 }}
            wrapperClassName="h-auto"
          />
          <Button className="w-full" onClick={() => setPopupAccountSearch(true)}>
            {t('Add')}
          </Button>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="py-2 text-xl">{t('Group Account List')}</div>
            {/* <Button onClick={() => setPopupGroupSearch(true)}>{t('Add')}</Button> */}
          </div>
          <Table
            columns={groupColumns}
            dataSource={[...groupList]}
            pagination={null}
            scroll={{ y: 500 }}
            wrapperClassName="h-auto"
          />
          <Button className="w-full" onClick={() => setPopupGroupSearch(true)}>
            {t('Add')}
          </Button>
        </>
      )}

      <Modal
        destroyOnClose
        className="vindr-modal procedure-search max-w-screen w-[1200px]"
        footer={null}
        maskClosable={false}
        open={popupAccountSearch}
        title={t('Select Account')}
        zIndex={1001}
        onCancel={() => setPopupAccountSearch(false)}
      >
        <AccountSearch
          initFilters={{ institutions: order?.institution?.code ? [order?.institution?.code] : [] }}
          selectedAccounts={accountList}
          onCancel={() => setPopupAccountSearch(false)}
          onSave={(accounts) => {
            setAccountList(accounts);
            setPopupAccountSearch(false);
          }}
        />
      </Modal>

      <Modal
        destroyOnClose
        className="vindr-modal procedure-search max-w-screen w-[1200px]"
        footer={null}
        maskClosable={false}
        open={popupGroupSearch}
        title={t('Select Group Account')}
        zIndex={1001}
        onCancel={() => setPopupGroupSearch(false)}
      >
        <GroupSearch
          initFilters={{ institution_ids: order?.institution_id ? [order?.institution_id] : [] }}
          selectedAccounts={accountList}
          onCancel={() => setPopupGroupSearch(false)}
          onSave={(groups) => {
            setGroupList(groups);
            setPopupGroupSearch(false);
          }}
        />
      </Modal>

      <div className="footer mt-3 flex justify-end">
        <Button onClick={onCancel}>{t('Cancel')}</Button>
        <Button className="ml-2" type="primary" onClick={handleSave}>
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default AssigningForm;
